<!--
 * @Author: AI Assistant
 * @Description: 脱敏字段展示组件，支持小眼睛切换查看明文
 * @FilePath: /src/components/global/SensitiveField.vue
-->
<template>
  <span class="sensitive-field">
    <template v-if="maskedValue">
      <span class="sensitive-field__text">
        {{ showPlain ? plainValue : maskedValue }}
      </span>
      <LoadingOutlined v-show="loading" class="sensitive-field__icon" />
      <EyeInvisibleTwoTone
        v-show="!loading && showPlain"
        @click="handleToggle"
        class="sensitive-field__eye"
        :two-tone-color="primaryColor"
      />
      <EyeTwoTone
        v-show="!loading && !showPlain"
        @click="handleToggle"
        class="sensitive-field__eye"
        :two-tone-color="primaryColor"
      />
    </template>
    <template v-else>--</template>
  </span>
</template>

<script lang="ts" setup>
import { computed, inject, reactive, ref, watch } from 'vue'
import { EyeTwoTone, EyeInvisibleTwoTone, LoadingOutlined } from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import { SensitiveFieldContext } from './SensitiveFieldProvider.vue'
import { sensitiveDataService } from '@/service/api/sensitiveData'
import { isSuccess, getPrimaryColor, lang } from '@/utils'

const primaryColor = getPrimaryColor()

interface Props {
  /** 脱敏后的展示值（如 138****1234） */
  maskedValue: string
  /** 加密字段值，传给解密接口的 fieldValue（如 xxxEncrypted 的值） */
  encryptedValue: string
  /** 查询字段名称，传给解密接口的 queriedFieldName（如 companyPhone） */
  queriedFieldName: string
  /** 业务标识，传给解密接口的 bizId（网点编码） */
  bizId?: string
  /** 二级菜单名称，传给解密接口的 moduleName */
  moduleName?: string
  /** 该字段在页面中的唯一标识（用于全局小眼睛状态管理） */
  fieldKey: string
}

const props = defineProps<Props>()

/** 注入全局上下文 */
const context = inject<SensitiveFieldContext>('sensitiveFieldContext')

if (!context) {
  console.warn('[SensitiveField] 必须在 SensitiveFieldProvider 内使用')
}

/** 组件内部状态 */
const state = reactive({
  loading: false,
  showPlain: false,
  plainValue: ''
})

/** 是否展示明文 */
const showPlain = computed(() => state.showPlain)

/** 监听全局 activeFieldKey 变化，若不再是当前字段则自动关闭 */
watch(() => context?.activeFieldKey, (newVal) => {
  if (newVal !== props.fieldKey && state.showPlain) {
    state.showPlain = false
  }
})

/** 点击小眼睛切换 */
const handleToggle = () => {
  if (!context) return

  if (state.showPlain) {
    // 当前已打开，关闭
    context.closeActiveField()
    state.showPlain = false
  } else {
    // 当前关闭，打开：先关闭其他
    context.toggleField(props.fieldKey)
    // 调用解密接口
    decryptData()
  }
}

/** 调用解密接口 */
const decryptData = async () => {
  if (!props.encryptedValue) {
    message.warning(lang('无加密数据'))
    return
  }

  state.loading = true
  try {
    const res = await sensitiveDataService.decrypt({
      fieldValue: props.encryptedValue,
      queriedFieldName: props.queriedFieldName,
      bizId: props.bizId,
      systemName: '财务结算',
      menuName: context?.menuName || '',
      moduleName: props.moduleName || '',
      uri: window.location.pathname
    })

    if (isSuccess(res)) {
      state.plainValue = res.data?.plainValue || ''
      state.showPlain = true
    } else {
      message.error(res?.msg || lang('解密失败'))
      state.showPlain = false
    }
  } catch (err) {
    console.error('解密接口异常:', err)
    message.error(lang('解密失败'))
    state.showPlain = false
  } finally {
    state.loading = false
  }
}
</script>

<style lang="less" scoped>
.sensitive-field {
  display: inline-flex;
  align-items: center;

  .sensitive-field__text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sensitive-field__eye {
    cursor: pointer;
    margin-left: 4px;
    font-size: 14px;
  }

  .sensitive-field__icon {
    margin-left: 4px;
    font-size: 14px;
    color: var(--primary-color);
  }
}
</style>
